import h from 'hyperscript';

import illustration from '@disy/cadenza-icons/empty-worksheet-illustration.svg';
import type { EventKey } from '@disy/lib/event-util';
import { on, unByKey } from '@disy/lib/event-util';
import { parseSvg } from '@disy/lib/svg';
import type { MenuItem } from '@disy/ui/menu';
import { separateElements } from '@disy/ui/separator';

import type { AddAttributeCommandPayload } from 'cadenza/api-client/workbook-command-payloads';
import { Command } from 'cadenza/api-client/workbook-commands';
import { getStore } from 'cadenza/store/cadenza-store';
import { observeStore } from 'cadenza/store/state-utils';
import { dataTransfer } from 'cadenza/utils/datatransfer';
import { showNoDefaultMapViewAvailableError } from 'cadenza/workbook/default-map-utils/default-map-utils';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';
import { isAnyDefaultMapViewAvailable, noDefaultMapIsAvailable } from 'cadenza/workbook/state/default-map.state';
import { createAddDataViewActions } from 'cadenza/workbook/workbook-add-new-view/workbook-add-new-view';
import { executeCommand } from 'cadenza/workbook/workbook-core-reducers.state';
import { getActiveWorksheetId, getAttributeById } from 'cadenza/workbook/workbook-core-selectors.state';
import type { GridAreaDto } from 'cadenza/workbook/worksheet/dto/grid-area-dto';

import './empty-view.css';
import i18n from './empty-view.properties';

export const COMPONENT_NAME = 'd-empty-view';
const CONTENT_OUTER_CLASS = `${COMPONENT_NAME}--outer-content`;
const CONTENT_INNER_CLASS = `${COMPONENT_NAME}--inner-content`;
const ILLUSTRATION_CLASS = `${COMPONENT_NAME}--illustration`;
const MENU_CLASS = `${COMPONENT_NAME}--menu`;
const MENU_ITEM_CLASS = `${COMPONENT_NAME}--menu-item`;
const MENU_SEPARATOR_CLASS = `${COMPONENT_NAME}--menu-separator`;

const LAYOUT: GridAreaDto = [0, 0, 23, 23];
const DROP_ALLOWED_CLASS = 'drop-allowed';

export class EmptyView extends HTMLElement {
  // ! – initialized in `connectedCallback`
  #eventKeys!: EventKey[];
  // ! – initialized in `connectedCallback`
  #resizeObserver!: ResizeObserver;
  #dragEnterCounter = 0; // counts dragenter on child elements

  async connectedCallback() {
    this.classList.add(COMPONENT_NAME, 'drop-area');
    this.#eventKeys = [
      on(this, 'dragover', (event) => {
        if (this.#dropAllowed) {
          // prevent dropping on other elements
          event.preventDefault();
        }
      }),
      on(this, 'dragenter', () => {
        this.#dragEnterCounter++;
        this.#updateDropAllowed();
      }),
      on(this, 'dragleave', () => {
        this.#dragEnterCounter--;
        if (this.#dragEnterCounter === 0) {
          this.#clearDropAllowedForDraggedAttribute();
        }
      }),
    ];

    observeStore(getStore(), isAnyDefaultMapViewAvailable, () => this.#render());

    this.#resizeObserver = new ResizeObserver(() => this.#onResize());
    this.#resizeObserver.observe(this.parentElement!);
    this.#onResize();
  }

  #render() {
    this.innerHTML = '';
    this.append(...createContent());
  }

  disconnectedCallback() {
    unByKey(this.#eventKeys);
    this.#resizeObserver.disconnect();
  }

  get #dropAllowed() {
    return this.classList.contains(DROP_ALLOWED_CLASS);
  }

  set #dropAllowed(value: boolean | undefined) {
    if (value === undefined) {
      this.classList.remove(DROP_ALLOWED_CLASS);
    } else {
      const dropAllowed = Boolean(value);
      this.classList.toggle(DROP_ALLOWED_CLASS, dropAllowed);
    }
  }

  get layout(): GridAreaDto {
    return LAYOUT;
  }

  #updateDropAllowed() {
    const attributeId = dataTransfer<{ attributeId: AttributeId }>()?.attributeId;
    this.#dropAllowed = Boolean(attributeId);
  }

  #clearDropAllowedForDraggedAttribute() {
    this.#dropAllowed = undefined;
  }

  async handleAttributeDrop(event: Event) {
    this.#updateDropAllowed();
    if (this.#dropAllowed) {
      event.preventDefault();

      const attributeId = dataTransfer<{ attributeId: AttributeId }>()?.attributeId;
      const commandPayload: AddAttributeCommandPayload = {
        attributeId,
        index: 0,
        worksheetId: getActiveWorksheetId(getStore().getState()),
      };

      const attribute = getAttributeById(getStore().getState(), attributeId);
      if (attribute.type === 'GEOMETRY') {
        if (noDefaultMapIsAvailable(getStore().getState())) {
          await showNoDefaultMapViewAvailableError();
          return;
        }
      }
      getStore().dispatch(executeCommand(Command.ADD_ATTRIBUTE(commandPayload)));
    }
    this.#clearDropAllowedForDraggedAttribute();
  }

  #onResize() {
    const width = this.offsetWidth;
    this.classList.remove('xs', 's', 'm', 'l');

    if (width < 300) {
      this.classList.add('xs');
    } else if (width < 450) {
      this.classList.add('s');
    } else if (width < 600) {
      this.classList.add('m');
    } else {
      this.classList.add('l');
    }

    const height = this.offsetHeight;
    this.classList.remove('v-xs', 'v-s', 'v-m', 'v-l');

    if (height < 300) {
      this.classList.add('v-xs');
    } else if (height < 450) {
      this.classList.add('v-s');
    } else if (height < 600) {
      this.classList.add('v-m');
    } else {
      this.classList.add('v-l');
    }
  }
}

customElements.define(COMPONENT_NAME, EmptyView);

function createContent() {
  return [
    h(
      `.${CONTENT_OUTER_CLASS}`,
      h(`.${CONTENT_INNER_CLASS}.d-stack-v.space-4`, [
        createIllustration(),
        h('h2', i18n('drag')),
        h('p.label-or', i18n('or')),
        h('p.label-menu', i18n('chooseFromMenu')),
        h('p.label-create-new-view', i18n('createNewView')),
        createMenu(),
      ]),
    ),
  ];
}

function createIllustration() {
  const svgEl = parseSvg(illustration);
  svgEl.classList.add(ILLUSTRATION_CLASS);
  return svgEl;
}

function createMenu() {
  const menuItems = createAddDataViewActions().map(createMenuItem);
  return h(`.${MENU_CLASS}`, { role: 'menu' }, separateElements(menuItems, { styleClass: MENU_SEPARATOR_CLASS }));
}

function createMenuItem(action: MenuItem) {
  return h(
    `.${MENU_ITEM_CLASS}.d-stack-h.space-1.keyboard-click`,
    {
      type: 'button',
      role: 'menuitem',
      tabIndex: '0',
      onclick: action.execute,
    },
    [action.icon, h('.span', action.label)],
  );
}
